import { cn } from "@/lib/cn";

export interface RankedBarRow {
  key: string;
  label: string;
  value: number;
  /** A `chart-tone--*` suffix: `cat-1`, `accent`, `neutral`, … */
  tone: string;
  /** Optional second line under the label - a share, a rate, a note. */
  hint?: string;
}

/**
 * Horizontal ranked bars, floored so a small count never renders as an
 * invisible hairline next to the row's tallest value - the same problem
 * visionai-app's dashboard solves with a log scale, done here with a linear
 * plus-floor scale instead, since these counts run in the tens rather than the
 * hundreds and don't need a log axis to stay readable.
 *
 * Horizontal rather than vertical because the categories are words, not dates.
 * "Vegetation clearance" under a vertical bar is either rotated or truncated;
 * beside a horizontal one it is just read.
 *
 * The share is shown alongside the count when a total is passed. A ranked list
 * answers "which is biggest"; the share answers "and does that matter", which is
 * usually the follow-up and always another click away without it.
 */
export function RankedBars({
  rows,
  total,
  minPercent = 4,
  className,
}: {
  rows: RankedBarRow[];
  /** Denominator for the share column. Omit to show counts only. */
  total?: number;
  minPercent?: number;
  className?: string;
}) {
  const max = Math.max(1, ...rows.map((row) => row.value));

  if (rows.length === 0) {
    return <p className="chart-empty">Nothing recorded in this window yet.</p>;
  }

  return (
    <ul className={cn("ranked", className)}>
      {rows.map((row) => {
        const percent =
          row.value === 0 ? 0 : Math.max(minPercent, Math.round((row.value / max) * 100));

        return (
          <li key={row.key} className={cn("ranked__row", `chart-tone--${row.tone}`)}>
            <span className="ranked__label">
              {row.label}
              {row.hint && <span className="ranked__hint">{row.hint}</span>}
            </span>

            <span className="ranked__track">
              {/* A computed width - the one thing `ui/CLAUDE.md` keeps inline
                  styles for. */}
              <span className="ranked__fill" style={{ width: `${percent}%` }} />
            </span>

            <span className="ranked__value tnum">{row.value}</span>
            {total != null && (
              <span className="ranked__share tnum">
                {total > 0 ? `${Math.round((row.value / total) * 100)}%` : "-"}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A bar inside a table cell - no axis, no label, just the proportion beside the
 * number it belongs to.
 *
 * Exists because a column of percentages is a column of numbers the reader has
 * to compare digit by digit; the same column with a 60px bar behind it is
 * scannable at a glance, which is the whole reason the scorecard is a table
 * rather than eight more charts.
 */
export function MiniBar({
  value,
  max = 100,
  tone = "accent",
  className,
}: {
  value: number;
  max?: number;
  tone?: string;
  className?: string;
}) {
  const percent = Math.max(0, Math.min(100, (value / Math.max(1, max)) * 100));

  return (
    <span className={cn("minibar", `chart-tone--${tone}`, className)} aria-hidden>
      <span className="minibar__fill" style={{ width: `${percent}%` }} />
    </span>
  );
}
